const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `23 Sep 2026` — the design system forbids ambiguous numeric dates. */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.getDate().toString().padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

export function formatTime(value: string | Date | null | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
}

export const formatDateTime = (value: string | Date | null | undefined) =>
  value ? `${formatDate(value)}, ${formatTime(value)}` : '—'

export function num(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return value.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

/** Areas read as 1.46 ha up close and 13,604 ha in aggregate. */
export function ha(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return '—'
  return `${num(value, value >= 1000 ? 0 : digits)} ha`
}

export function pct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return '—'
  return `${num(value, digits)}%`
}

export function bytes(value: number | null | undefined): string {
  if (!value) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let n = value
  let i = 0
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i++
  }
  return `${n.toFixed(n >= 100 || i === 0 ? 0 : 1)} ${units[i]}`
}

export const signed = (n: number) => (n > 0 ? `+${n}` : n === 0 ? '0' : `−${Math.abs(n)}`)

/** `5.7988°N, 6.6014°W` */
export function formatLngLat(lng: number, lat: number): string {
  const ns = lat >= 0 ? 'N' : 'S'
  const ew = lng >= 0 ? 'E' : 'W'
  return `${Math.abs(lat).toFixed(4)}°${ns}, ${Math.abs(lng).toFixed(4)}°${ew}`
}

/** `1:3k` — the scale readout in the map's bottom-right corner. */
export function formatScale(zoom: number, lat: number): string {
  const metresPerPixel = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom
  const scale = Math.round(metresPerPixel * 96 * 39.37)
  if (scale >= 1_000_000) return `1:${Math.round(scale / 1_000_000)}M`
  if (scale >= 1000) return `1:${Math.round(scale / 1000)}k`
  return `1:${scale}`
}

/**
 * A readable list of names, truncated so a file naming a dozen suppliers does
 * not push everything else off the line.
 */
export function listNames(names: string[], max = 3): string {
  if (names.length <= max) {
    return names.length < 2
      ? (names[0] ?? '')
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  }
  return `${names.slice(0, max).join(', ')} and ${names.length - max} more`
}

/** Lowercased and stripped of accents, for matching what people type: nobody types Séguéla with the accents. */
export const fold = (value: string) =>
  value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
